import { cva } from '@/styled-system/css';

export const progressStepperDotRecipe = cva({
  base: {
    'position': 'relative',
    'display': 'flex',
    'alignItems': 'center',
    'justifyContent': 'center',
    'borderRadius': 'shape.ellipse',

    '&:before': {
      content: '""',
      display: 'none',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transformOrigin: 'center center',
      height: 'ProgressMeter.Node.focusRing.active',
      width: 'ProgressMeter.Node.focusRing.active',
      background: 'ProgressMeter.Node.active.bg',
      borderRadius: 'shape.ellipse',
      opacity: 'action.focus',
      zIndex: '-1',
    },
  },
  variants: {
    interactive: {
      true: {
        '&:before': {
          display: 'block',
        },
      },
      false: {},
    },
    variant: {
      complete: {
        '--c11n-tone-color': '{colors.ProgressMeter.Node.complete.bg}',
        'background': 'ProgressMeter.Node.complete.bg',
        'height': 'handle.sm',
        'width': 'handle.sm',
      },
      incomplete: {
        '--c11n-tone-color': '{colors.ProgressMeter.Node.incomplete.bg}',
        'backgroundColor': 'ProgressMeter.Node.incomplete.bg',
        'height': 'handle.sm',
        'width': 'handle.sm',
      },
      critical: {},
      warning: {},
      notStarted: {
        height: 'indicator.xs',
        width: 'indicator.xs',
        background: 'ProgressMeter.Node.inactive.icon',
      },
      active: {
        height: 'handle.sm',
        width: 'handle.sm',
        boxShadow: 'none',
        background: 'ProgressMeter.Node.active.icon',
        borderStyle: 'solid',
        borderWidth: 'control.selected',
        borderColor: 'ProgressMeter.Node.active.border',
      },
      none: {},
    },
    alignLabel: {
      left: {},
      center: {},
    },
    orientation: {
      horizontal: {},
      vertical: {
        insetBlockStart: 'calc((1.5rem - {sizes.indicator.xs}) / 2)',
      },
    },
  },
  compoundVariants: [
    {
      variant: ['critical', 'warning'],
      css: {
        'boxShadow': 'none',
        'background': 'bg.invisible',
        'height': 'handle.sm',
        'width': 'handle.sm',

        // Scale the icon up to make it easier to see
        '& svg': {
          transform: 'scale(1.2)',
        },

        // White background fill behind the icon
        '&:after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: 'calc({sizes.handle.sm} / 5)',
          left: '50%',
          transform: 'scale(1.2) translateX(-50%)',
          width: 'calc({sizes.handle.sm} / 5)',
          height: 'calc({sizes.handle.sm} * 0.6)',
          background: 'ProgressMeter.Node.warning.icon',
          zIndex: '-1',
        },
      },
    },
    {
      orientation: 'vertical',
      variant: 'notStarted',
      css: {
        marginInlineStart: '{sizes.pipe.weight.thickest}',
        insetBlockStart: 'calc(({lineHeights.body-sm} - {sizes.indicator.xs}) / 2)',
      },
    },
    {
      orientation: 'vertical',
      variant: ['active', 'complete', 'incomplete', 'critical', 'warning'],
      css: {
        insetBlockStart: 'calc(({lineHeights.body-sm} - {sizes.handle.sm}) / 2)',
      },
    },
    {
      orientation: 'horizontal',
      alignLabel: 'left',
      variant: 'notStarted',
      css: {
        // Nudge small dot so it looks ok at beginning and end of bar
        marginInlineStart: 'calc(({sizes.pipe.weight.thickest} - {sizes.indicator.xs}) / 2)',
      },
    },
    // Handles interactive scale
    {
      variant: ['complete', 'critical', 'incomplete', 'notStarted', 'warning'], // Skip active
      interactive: true,
      css: {
        '&:before': {
          transform: 'translate(-50%, -50%) scale(0)',
          transitionProperty: {
            _motionReduce: 'none',
            _motionSafe: 'height, transform, width',
          },
          transitionDuration: {
            _motionReduce: '0s',
            _motionSafe: '{durations.200}',
          },
          transitionTimingFunction: '{easings.ease}',
        },
        'a:is(:hover) &, a:is(:focus) &, a:is(:hover) &, a:is(:focus) &': {
          '&:before': {
            transform: 'translate(-50%, -50%) scale(1)',
          },
        },
      },
    },
    // Sets non-notStarted sizes
    {
      variant: ['complete', 'critical', 'incomplete', 'notStarted', 'warning'], // Skip active
      interactive: true,
      css: {
        'a:is(:hover) &, a:is(:focus) &, a:is(:hover) &, a:is(:focus) &': {
          '&:before': {
            height: 'ProgressMeter.Node.focusRing.default.hover',
            width: 'ProgressMeter.Node.focusRing.default.hover',
          },
        },
        'a:is(:active) &, a:is(:active) &': {
          '&:before': {
            height: 'ProgressMeter.Node.focusRing.default.pressed',
            width: 'ProgressMeter.Node.focusRing.default.pressed',
          },
        },
      },
    },
    {
      variant: ['notStarted'],
      interactive: true,
      css: {
        'a:is(:hover) &, a:is(:focus) &, a:is(:hover) &, a:is(:focus) &': {
          '&:before': {
            height: 'handle.sm',
            width: 'handle.sm',
          },
        },
        'a:is(:active) &, a:is(:active) &': {
          '&:before': {
            height: 'handle.sm',
            width: 'handle.sm',
          },
        },
      },
    },
    {
      variant: 'active',
      interactive: [true, false],
      css: {
        '&:before': {
          display: 'block',
          transform: 'translate(-50%, -50%) scale(1)',
        },
      },
    },
  ],
});
